/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import {
  Grid3X3, Plus, Trash2, Pencil, Search,
  ChevronLeft, ChevronRight, CalendarRange, Info,
} from "lucide-react";
import TicTacToeCellAnswersDialog from "@/pages/quiz/TicTacToeCellAnswersDialog";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

interface TicTacToeCell {
  id: number;
  gridId: number;
  row: number;
  col: number;
  validPlayers: string;
  answers?: { id: number; playerId: number }[];
}

interface TicTacToeGrid {
  id: number;
  name: string | null;
  col0: string; col1: string; col2: string;
  row0: string; row1: string; row2: string;
  fromYear: number | null;
  toYear: number | null;
  eraLabel: string | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  cells: TicTacToeCell[];
}

type CellInputs = Record<string, string>;

const makeEmptyCells = (): CellInputs => {
  const out: CellInputs = {};
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) out[`${r}_${c}`] = "";
  return out;
};

const emptyCreateForm = {
  name: "",
  col0: "", col1: "", col2: "",
  row0: "", row1: "", row2: "",
  fromYear: "", toYear: "",
  eraLabel: "", description: "",
  cells: makeEmptyCells(),
};

const emptyEditForm = {
  name: "",
  col0: "", col1: "", col2: "",
  row0: "", row1: "", row2: "",
  fromYear: "", toYear: "",
  eraLabel: "", description: "",
};

const PAGE_SIZE = 8;

/** Mini grid preview — cells clickable to open answer manager */
function GridPreview({
  grid,
  onCellClick,
}: {
  grid: TicTacToeGrid;
  onCellClick: (cellId: number) => void;
}) {
  const cols = [grid.col0, grid.col1, grid.col2];
  const rows = [grid.row0, grid.row1, grid.row2];

  const findCell = (r: number, c: number) =>
    grid.cells.find((ce) => ce.row === r && ce.col === c);

  const answerCount = (r: number, c: number) => {
    const cell = findCell(r, c);
    if (!cell) return 0;
    if ((cell as any).answers?.length > 0) return (cell as any).answers.length;
    try { return (JSON.parse(cell.validPlayers) as string[]).length; } catch { return 0; }
  };

  const totalFilled = grid.cells.filter((ce) => {
    try { return (JSON.parse(ce.validPlayers) as string[]).length > 0 || (ce as any).answers?.length > 0; } catch { return false; }
  }).length;

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="border-collapse text-xs w-full">
          <thead>
            <tr>
              <th className="bg-gray-100 border border-gray-200 p-2 w-24 min-w-[80px]" />
              {cols.map((col, i) => (
                <th key={i} className="bg-indigo-700 text-white border border-indigo-600 p-2 text-center font-semibold min-w-[90px]">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                <td className="bg-indigo-700 text-white border border-indigo-600 p-2 font-semibold text-center">
                  {row}
                </td>
                {[0, 1, 2].map((ci) => {
                  const n = answerCount(ri, ci);
                  const cell = findCell(ri, ci);
                  return (
                    <td
                      key={ci}
                      onClick={() => cell && onCellClick(cell.id)}
                      title={cell ? "Click to manage answers" : undefined}
                      className={cn(
                        "border border-gray-200 p-2 text-center transition-all select-none",
                        cell ? "cursor-pointer" : "",
                        n > 0
                          ? "bg-green-50 text-green-700 hover:bg-green-100"
                          : "bg-red-50 text-red-400 hover:bg-red-100",
                      )}
                    >
                      <span className={cn("font-semibold", n > 0 ? "text-green-700" : "text-red-400")}>
                        {n > 0 ? n : "—"}
                      </span>
                      {n > 0 && <span className="text-gray-400 ml-0.5 font-normal">p</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-indigo-500 mt-1.5 flex items-center gap-1">
        <span className="font-semibold">{totalFilled}/9</span> cells have answers · click any cell to manage
      </p>
    </div>
  );
}

export default function TicTacToePage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createErrors, setCreateErrors] = useState<{ teams?: string; years?: string }>({});
  const [editingGrid, setEditingGrid] = useState<TicTacToeGrid | null>(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editErrors, setEditErrors] = useState<{ teams?: string; years?: string }>({});
  const [activeCellId, setActiveCellId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<TicTacToeGrid[]>({
    queryKey: ["quiz", "tictactoe"],
    queryFn: async () => {
      const r = await apiClient.get(API_ENDPOINTS.QUIZ.TTT_LIST) as unknown as TicTacToeGrid[] | null;
      return r ?? [];
    },
  });
  const grids = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quiz", "tictactoe"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) => apiClient.post(API_ENDPOINTS.QUIZ.TTT_CREATE, d),
    onSuccess: () => { toast.success("Grid created"); invalidate(); setCreateOpen(false); setCreateForm(emptyCreateForm); setCreateErrors({}); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to create"),
  });

  const { mutate: updateGrid, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.TTT_UPDATE(id), d),
    onSuccess: () => { toast.success("Grid updated"); invalidate(); setEditingGrid(null); setEditErrors({}); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to update"),
  });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.TTT_UPDATE(id), { isActive }),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.QUIZ.TTT_DELETE(id)),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to delete"),
  });

  const openEdit = (grid: TicTacToeGrid) => {
    setEditingGrid(grid);
    setEditForm({
      name: grid.name ?? "",
      col0: grid.col0, col1: grid.col1, col2: grid.col2,
      row0: grid.row0, row1: grid.row1, row2: grid.row2,
      fromYear: grid.fromYear?.toString() ?? "",
      toYear: grid.toYear?.toString() ?? "",
      eraLabel: grid.eraLabel ?? "",
      description: grid.description ?? "",
    });
  };

  const handleCreate = () => {
    const newErrors: typeof createErrors = {};
    const teams = [createForm.col0, createForm.col1, createForm.col2, createForm.row0, createForm.row1, createForm.row2];
    if (teams.some((t) => !t.trim())) newErrors.teams = "All 6 team names are required";
    else if (new Set(teams.map((t) => t.trim().toLowerCase())).size !== 6) newErrors.teams = "All 6 team names must be unique";
    const from = createForm.fromYear ? Number(createForm.fromYear) : null;
    const to = createForm.toYear ? Number(createForm.toYear) : null;
    if (from !== null && (isNaN(from) || from < 1800 || from > 2100)) newErrors.years = "From Year must be between 1800 and 2100";
    else if (to !== null && (isNaN(to) || to < 1800 || to > 2100)) newErrors.years = "To Year must be between 1800 and 2100";
    else if (from !== null && to !== null && from > to) newErrors.years = "From Year must be ≤ To Year";
    if (Object.keys(newErrors).length) { setCreateErrors(newErrors); return; }
    setCreateErrors({});
    const cells: { row: number; col: number; validPlayers: string[] }[] = [];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        const raw = createForm.cells[`${r}_${c}`];
        cells.push({
          row: r, col: c,
          validPlayers: raw.split(",").map((p) => p.trim().toLowerCase()).filter(Boolean),
        });
      }
    }
    create({
      name: createForm.name.trim() || null,
      col0: createForm.col0.trim(), col1: createForm.col1.trim(), col2: createForm.col2.trim(),
      row0: createForm.row0.trim(), row1: createForm.row1.trim(), row2: createForm.row2.trim(),
      fromYear: createForm.fromYear ? Number(createForm.fromYear) : null,
      toYear: createForm.toYear ? Number(createForm.toYear) : null,
      eraLabel: createForm.eraLabel.trim() || null,
      description: createForm.description.trim() || null,
      cells,
    });
  };

  const handleUpdate = () => {
    if (!editingGrid) return;
    const newErrors: typeof editErrors = {};
    const teams = [editForm.col0, editForm.col1, editForm.col2, editForm.row0, editForm.row1, editForm.row2];
    if (teams.some((t) => !t.trim())) newErrors.teams = "All 6 team names are required";
    else if (new Set(teams.map((t) => t.trim().toLowerCase())).size !== 6) newErrors.teams = "All 6 team names must be unique";
    const from = editForm.fromYear ? Number(editForm.fromYear) : null;
    const to = editForm.toYear ? Number(editForm.toYear) : null;
    if (from !== null && (isNaN(from) || from < 1800 || from > 2100)) newErrors.years = "From Year must be between 1800 and 2100";
    else if (to !== null && (isNaN(to) || to < 1800 || to > 2100)) newErrors.years = "To Year must be between 1800 and 2100";
    else if (from !== null && to !== null && from > to) newErrors.years = "From Year must be ≤ To Year";
    if (Object.keys(newErrors).length) { setEditErrors(newErrors); return; }
    setEditErrors({});
    updateGrid({
      id: editingGrid.id,
      d: {
        name: editForm.name.trim() || null,
        col0: editForm.col0.trim(), col1: editForm.col1.trim(), col2: editForm.col2.trim(),
        row0: editForm.row0.trim(), row1: editForm.row1.trim(), row2: editForm.row2.trim(),
        fromYear: editForm.fromYear ? Number(editForm.fromYear) : null,
        toYear: editForm.toYear ? Number(editForm.toYear) : null,
        eraLabel: editForm.eraLabel.trim() || null,
        description: editForm.description.trim() || null,
      },
    });
  };

  const eraStr = (g: TicTacToeGrid) => {
    if (g.eraLabel) return g.eraLabel;
    if (g.fromYear && g.toYear) return `${g.fromYear}–${g.toYear}`;
    if (g.fromYear) return `${g.fromYear}+`;
    if (g.toYear) return `up to ${g.toYear}`;
    return null;
  };

  const filtered = grids.filter((g) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (g.name ?? "").toLowerCase().includes(s) ||
      [g.col0, g.col1, g.col2, g.row0, g.row1, g.row2].some((t) => t.toLowerCase().includes(s))
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const activeCount = grids.filter((g) => g.isActive).length;

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Grid3X3 className="h-8 w-8 text-indigo-500" />
            Football Grid
          </h1>
          <p className="text-gray-500 mt-1">
            {grids.length} grids · {activeCount} active
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          New Grid
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{grids.length}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Grids</p>
        </div>
        <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
          <p className="text-xs text-gray-500 mt-0.5">Active</p>
        </div>
        <div className="bg-white rounded-xl border shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-gray-400">{grids.length - activeCount}</p>
          <p className="text-xs text-gray-500 mt-0.5">Inactive</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name or team…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {/* Grid list */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Grid3X3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{search ? "No matches" : "No grids yet — create your first one"}</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {paged.map((grid) => (
              <div
                key={grid.id}
                className={cn(
                  "bg-white rounded-xl border shadow-sm p-5",
                  !grid.isActive && "opacity-60",
                )}
              >
                {/* Card header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 text-base">
                        {grid.name || <span className="text-gray-400 font-normal italic">Unnamed Grid</span>}
                      </h3>
                      {eraStr(grid) && (
                        <Badge className="bg-indigo-100 text-indigo-700 gap-1">
                          <CalendarRange className="h-3 w-3" />
                          {eraStr(grid)}
                        </Badge>
                      )}
                      <Badge className={grid.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}>
                        {grid.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    {grid.description && (
                      <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                        <Info className="h-3 w-3 mt-0.5 shrink-0" />
                        {grid.description}
                      </p>
                    )}
                    <div className="flex gap-4 mt-1.5 text-xs text-gray-400">
                      <span>Cols: <span className="text-gray-600 font-medium">{grid.col0} · {grid.col1} · {grid.col2}</span></span>
                      <span>Rows: <span className="text-gray-600 font-medium">{grid.row0} · {grid.row1} · {grid.row2}</span></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Switch
                        checked={grid.isActive}
                        onCheckedChange={(v) => toggle({ id: grid.id, isActive: v })}
                        className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400"
                      />
                    </div>
                    <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => openEdit(grid)}>
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="h-8 w-8 p-0"
                      onClick={() => { if (confirm(`Delete "${grid.name || "this grid"}"?`)) remove(grid.id); }}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>

                {/* Grid preview */}
                <GridPreview grid={grid} onCellClick={setActiveCellId} />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-5 text-sm text-gray-500">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === "…" ? (
                      <span key={`e-${i}`} className="px-1">…</span>
                    ) : (
                      <Button
                        key={n}
                        variant={n === safePage ? "default" : "outline"}
                        size="sm"
                        className={cn("h-8 w-8 p-0", n === safePage && "bg-indigo-600 hover:bg-indigo-700 text-white")}
                        onClick={() => setPage(n as number)}
                      >
                        {n}
                      </Button>
                    )
                  )}
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Cell answers dialog */}
      <TicTacToeCellAnswersDialog
        open={activeCellId !== null}
        cellId={activeCellId}
        onOpenChange={(o) => !o && setActiveCellId(null)}
      />

      {/* ── Edit Grid Dialog ── */}
      <Dialog open={!!editingGrid} onOpenChange={(v) => { if (!v) { setEditingGrid(null); setEditErrors({}); } }}>
        <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Grid — {editingGrid?.name || "Unnamed"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">Grid Name (optional)</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Premier League Legends"
              />
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Column Teams (top headers) <span className="text-red-500">*</span></p>
              <div className="grid grid-cols-3 gap-2">
                {(["col0", "col1", "col2"] as const).map((k, i) => (
                  <div key={k}>
                    <Label className="mb-1 block text-xs text-gray-500">Col {i + 1}</Label>
                    <Input
                      value={editForm[k]}
                      onChange={(e) => { setEditForm((f) => ({ ...f, [k]: e.target.value })); setEditErrors((er) => ({ ...er, teams: undefined })); }}
                      placeholder={["Man United", "Liverpool", "Arsenal"][i]}
                      className={editErrors.teams && !editForm[k].trim() ? "border-red-400" : ""}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Row Teams (left headers) <span className="text-red-500">*</span></p>
              <div className="grid grid-cols-3 gap-2">
                {(["row0", "row1", "row2"] as const).map((k, i) => (
                  <div key={k}>
                    <Label className="mb-1 block text-xs text-gray-500">Row {i + 1}</Label>
                    <Input
                      value={editForm[k]}
                      onChange={(e) => { setEditForm((f) => ({ ...f, [k]: e.target.value })); setEditErrors((er) => ({ ...er, teams: undefined })); }}
                      placeholder={["Chelsea", "Man City", "Tottenham"][i]}
                      className={editErrors.teams && !editForm[k].trim() ? "border-red-400" : ""}
                    />
                  </div>
                ))}
              </div>
              {editErrors.teams && <p className="text-xs text-red-500 mt-1.5 font-medium">{editErrors.teams}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-sm">From Year</Label>
                <Input
                  type="number"
                  value={editForm.fromYear}
                  onChange={(e) => { setEditForm((f) => ({ ...f, fromYear: e.target.value })); setEditErrors((er) => ({ ...er, years: undefined })); }}
                  placeholder="2000"
                  className={editErrors.years ? "border-red-400" : ""}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">To Year</Label>
                <Input
                  type="number"
                  value={editForm.toYear}
                  onChange={(e) => { setEditForm((f) => ({ ...f, toYear: e.target.value })); setEditErrors((er) => ({ ...er, years: undefined })); }}
                  placeholder="2026"
                  className={editErrors.years ? "border-red-400" : ""}
                />
              </div>
            </div>
            {editErrors.years && <p className="text-xs text-red-500 font-medium">{editErrors.years}</p>}
            <div>
              <Label className="mb-1 block text-sm">Era Label <span className="text-gray-400 font-normal">(optional — shown to players)</span></Label>
              <Input
                value={editForm.eraLabel}
                onChange={(e) => setEditForm((f) => ({ ...f, eraLabel: e.target.value }))}
                placeholder="e.g. 2000–2026"
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Description <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="Note shown to players above the grid"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingGrid(null)}>Cancel</Button>
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isUpdating ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Create Grid Dialog ── */}
      <Dialog open={createOpen} onOpenChange={(v) => { if (!v) { setCreateOpen(false); setCreateForm(emptyCreateForm); setCreateErrors({}); } }}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Football Grid</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label className="mb-1 block text-sm">Grid Name <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Premier League Legends"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-sm">From Year</Label>
                <Input
                  type="number"
                  value={createForm.fromYear}
                  onChange={(e) => { setCreateForm((f) => ({ ...f, fromYear: e.target.value })); setCreateErrors((er) => ({ ...er, years: undefined })); }}
                  placeholder="2000"
                  className={createErrors.years ? "border-red-400" : ""}
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">To Year</Label>
                <Input
                  type="number"
                  value={createForm.toYear}
                  onChange={(e) => { setCreateForm((f) => ({ ...f, toYear: e.target.value })); setCreateErrors((er) => ({ ...er, years: undefined })); }}
                  placeholder="2026"
                  className={createErrors.years ? "border-red-400" : ""}
                />
              </div>
            </div>
            {createErrors.years && <p className="text-xs text-red-500 font-medium">{createErrors.years}</p>}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-sm">Era Label <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  value={createForm.eraLabel}
                  onChange={(e) => setCreateForm((f) => ({ ...f, eraLabel: e.target.value }))}
                  placeholder="e.g. Modern Era"
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  value={createForm.description}
                  onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Note shown to players"
                />
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Column Teams <span className="text-gray-400 font-normal text-xs">(top headers)</span> <span className="text-red-500">*</span></p>
              <div className="grid grid-cols-3 gap-2">
                {(["col0", "col1", "col2"] as const).map((k, i) => (
                  <div key={k}>
                    <Label className="mb-1 block text-xs text-gray-500">Column {i + 1}</Label>
                    <Input
                      value={createForm[k]}
                      onChange={(e) => { setCreateForm((f) => ({ ...f, [k]: e.target.value })); setCreateErrors((er) => ({ ...er, teams: undefined })); }}
                      placeholder={["Man United", "Liverpool", "Arsenal"][i]}
                      className={createErrors.teams && !createForm[k].trim() ? "border-red-400" : ""}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Row Teams <span className="text-gray-400 font-normal text-xs">(left headers)</span> <span className="text-red-500">*</span></p>
              <div className="grid grid-cols-3 gap-2">
                {(["row0", "row1", "row2"] as const).map((k, i) => (
                  <div key={k}>
                    <Label className="mb-1 block text-xs text-gray-500">Row {i + 1}</Label>
                    <Input
                      value={createForm[k]}
                      onChange={(e) => { setCreateForm((f) => ({ ...f, [k]: e.target.value })); setCreateErrors((er) => ({ ...er, teams: undefined })); }}
                      placeholder={["Chelsea", "Man City", "Tottenham"][i]}
                      className={createErrors.teams && !createForm[k].trim() ? "border-red-400" : ""}
                    />
                  </div>
                ))}
              </div>
              {createErrors.teams && <p className="text-xs text-red-500 mt-1.5 font-medium">{createErrors.teams}</p>}
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-1">Valid Players per Cell <span className="text-gray-400 font-normal">(optional)</span></p>
              <p className="text-xs text-gray-500 mb-3 bg-blue-50 border border-blue-100 rounded p-2">
                💡 Leave empty and use the <strong>cell click</strong> feature after creation — the AI suggest tool will automatically find valid players from career data.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[0, 1, 2].map((ri) =>
                  [0, 1, 2].map((ci) => {
                    const key = `${ri}_${ci}`;
                    const rowLabel = createForm[`row${ri}` as keyof typeof createForm] as string || `Row ${ri + 1}`;
                    const colLabel = createForm[`col${ci}` as keyof typeof createForm] as string || `Col ${ci + 1}`;
                    return (
                      <div key={key}>
                        <Label className="mb-1 block text-[10px] text-gray-400 truncate">
                          {rowLabel} × {colLabel}
                        </Label>
                        <Textarea
                          value={createForm.cells[key]}
                          onChange={(e) => setCreateForm((f) => ({ ...f, cells: { ...f.cells, [key]: e.target.value } }))}
                          placeholder="e.g. Rooney, Scholes"
                          rows={2}
                          className="resize-none text-xs"
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); setCreateForm(emptyCreateForm); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isCreating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isCreating ? "Creating…" : "Create Grid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
